/**
 * Where the session lives: in memory, mirrored to sessionStorage so a reload of the
 * same tab keeps you signed in. Never localStorage: the token must not outlive the
 * tab or be shared with other tabs. Role and facility are read only from here (from
 * the server's sign-in answer and /auth/me), never from a URL.
 */
import { toRole, type Role } from '@aithora/shared';

export interface Session {
  token: string;
  expiresAt: string | null;
  user: {
    id: string;
    name: string | null;
    email: string | null;
    /** Exactly what the server sent. */
    rawRole: string;
    /** Known role, or null for one the console does not know (it then shows nothing). */
    role: Role | null;
    organizationId: string | null;
    orgName: string | null;
    /**
     * The whole scope is one seeded demo building (server /auth/me demo_building, or
     * the building viewed as). The console shows one "Demo building" banner then.
     */
    demoBuilding?: boolean;
    /** The language saved to the account (server /auth/me lang), or null if never picked. */
    lang?: 'en' | 'es' | null;
    /** The "First time here" banner was shown or dismissed (server /auth/me tutorial_seen). */
    tutorialSeen?: boolean;
    /** How many times this person has signed in (server /auth/me signin_count); the welcome banner shows on the first three. */
    signinCount?: number;
    /** The building's own time zone (server /auth/me time_zone), for the greeting; none = this device's clock. */
    timeZone?: string | null;
  };
}

export const SESSION_KEY = 'aithora.console.session';

let memory: Session | null = null;

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function isExpired(s: Session): boolean {
  if (!s.expiresAt) return false;
  const t = Date.parse(s.expiresAt);
  return Number.isFinite(t) && t <= Date.now();
}

export function readSession(): Session | null {
  if (memory) return isExpired(memory) ? null : memory;
  const raw = storage()?.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Session;
    if (!s || typeof s.token !== 'string' || !s.user) return null;
    // Re-derive the role from the stored server value, so an edited storage entry
    // can never name a role the server did not send.
    s.user.role = toRole(s.user.rawRole);
    if (isExpired(s)) {
      clearSession();
      return null;
    }
    memory = s;
    return s;
  } catch {
    clearSession();
    return null;
  }
}

export function writeSession(s: Session): void {
  memory = s;
  try {
    storage()?.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    /* private mode: memory still holds it */
  }
}

/**
 * Browser storage that holds a person's own work, which can name residents (PHI): the
 * Atlas chat thread and the To do note drafts. Each is kept in this tab's sessionStorage
 * under a key per user (userKey), so someone else signing in at the same workstation never
 * sees it, and all of it is removed whenever a session ends (sign-out, expiry, refused).
 */
export const PER_USER_KEYS = ['aithora.atlas.thread', 'aithora.wq.drafts'] as const;

/** The storage key for one person's copy of a PER_USER_KEYS entry; null with nobody signed in. */
export function userKey(base: (typeof PER_USER_KEYS)[number], userId: string | null | undefined): string | null {
  return userId ? `${base}:${userId}` : null;
}

/** Removes every person's copy (and any older shared copy) from both storages. */
export function clearPerUserData(): void {
  for (const pick of [() => window.sessionStorage, () => window.localStorage]) {
    try {
      const s = typeof window !== 'undefined' ? pick() : null;
      if (!s) continue;
      const doomed: string[] = [];
      for (let i = 0; i < s.length; i++) {
        const k = s.key(i);
        if (k && PER_USER_KEYS.some((b) => k === b || k.startsWith(`${b}:`))) doomed.push(k);
      }
      for (const k of doomed) s.removeItem(k);
    } catch {
      /* storage blocked: nothing was kept there */
    }
  }
}

export function clearSession(): void {
  memory = null;
  try {
    storage()?.removeItem(SESSION_KEY);
  } catch {
    /* nothing to clear */
  }
  clearPerUserData();
}

export function currentToken(): string | null {
  return readSession()?.token ?? null;
}
